import React, { useState } from "react";
import { TerminalTheme, Agent } from "../types";
import { MobileChatTerminal } from "./MobileChatTerminal";
import { MobileOutboundDrawer } from "./MobileOutboundDrawer";
import { MobileDocsDrawer } from "./MobileDocsDrawer";
import { MobileDocViewerPane } from "./MobileDocViewerPane";
// MobileQuickAskFab removed from /chat per Terrell 2026-05-26 — Gemini
// quick-ask is a dashboard concept; the mobile chat companion should be
// purely conversational with the agent. Import kept available in
// components/ in case it's wanted back later.

/**
 * ChatOnlyPage — minimal Tailscale-gated companion app served at /chat.
 *
 * Reuses the full AgentChatTerminal (real chat with file uploads, SSE
 * updates, fleet-scoped routing). Strips everything else (sidebar, tabs,
 * widgets, footers) to maximize chat surface area.
 *
 * Mobile-first design:
 *   - h-[100dvh] = dynamic viewport height (iOS Safari URL bar resilient)
 *   - Header collapses padding on mobile, expands on desktop
 *   - Footer status pill hidden on phones (more chat area), visible on sm+
 *   - PWA-installable (see index.html manifest + iOS apple-touch icons)
 *   - Safe-area insets handled by index.css scoped to #chat-only-root
 *   - Tiny utility fonts (text-[10px]/[11px]/[12px]) inside the embedded
 *     chat terminal are bumped to mobile-readable via scoped CSS
 *   - AgentChatTerminal's desktop-baked h-[600px] gets overridden by CSS
 *     so it fills the flex-1 main area edge-to-edge
 */

interface ChatOnlyPageProps {
  theme: TerminalTheme;
  agents: Agent[];
  selectedAgentId: string | null;
  onSelectAgent: (id: string | null) => void;
  scopedFetch?: (path: string, init?: RequestInit) => Promise<Response>;
  canSend: boolean;
  selectedThemeId: string;
  onSelectTheme: (id: string) => void;
}

export const ChatOnlyPage: React.FC<ChatOnlyPageProps> = ({
  theme,
  agents,
  selectedAgentId,
  onSelectAgent,
  scopedFetch,
  canSend,
  selectedThemeId,
  onSelectTheme,
}) => {
  // Resolve the label to show in the chat header avatar/name.
  // Priority: explicit pick → local self (the agent we're served from) →
  // first agent in the list → "Agent". Never show "All agents" in chat —
  // there's only one bubble stream and it has a single owner.
  const selectedAgent =
    agents.find((a) => a.id === selectedAgentId) ||
    agents.find((a) => (a as any)._localOnly === true) ||
    agents[0] ||
    null;
  const agentLabel = selectedAgent?.name || "Agent";

  // Outbound-preview bottom-sheet visibility. Owned here so the sheet
  // renders as a sibling of MobileChatTerminal — that keeps it above the
  // chat surface and lets it claim its own touch + safe-area territory.
  const [showDrafts, setShowDrafts] = useState(false);

  // Documents drawer (Pinned + Recent) and the doc viewer pane it surfaces.
  // openDocFromDocs holds the filename to render in MobileDocViewerPane when
  // a card in the drawer is tapped. The drawer closes itself first so the
  // viewer slides up over an empty backdrop (single visible sheet at a time).
  const [showDocs, setShowDocs] = useState(false);
  // Single state for any doc viewer — fed by either the docs-drawer card tap
  // OR the in-chat doc-pill tap (DOC_REF_RE auto-detection in MobileChatTerminal).
  const [openDocFromDocs, setOpenDocFromDocs] = useState<string | null>(null);

  return (
    <div
      style={{
        backgroundColor: theme.bg,
        color: theme.fg,
      }}
      className="h-[100dvh] flex flex-col font-sans overflow-hidden"
      id="chat-only-root"
    >
      {/* MobileChatTerminal owns its own iOS Messages-style header now
       *  (avatar + name + drawer). Theme switcher + push toggle moved
       *  into the agent drawer's Settings tab. The outer ChatOnlyPage
       *  is just a full-height host. */}
      <main className="flex-1 overflow-hidden min-h-0">
        <MobileChatTerminal
          theme={theme}
          agentLabel={agentLabel}
          scopedFetch={scopedFetch}
          canSend={canSend}
          principal="Operator"
          selectedThemeId={selectedThemeId}
          onSelectTheme={onSelectTheme}
          onOpenDrafts={() => setShowDrafts(true)}
          onOpenDocs={() => setShowDocs(true)}
          onDocClick={(filename) => setOpenDocFromDocs(filename)}
        />
      </main>

      {/* Quick-ask FAB removed 2026-05-26 — chat companion is purely
          conversational with the agent; Gemini lives on the dashboard. */}

      {showDrafts && (
        <MobileOutboundDrawer
          theme={theme}
          scopedFetch={scopedFetch}
          onClose={() => setShowDrafts(false)}
        />
      )}

      {/* Docs drawer (Pinned + Recent). Tapping a card hands the filename to
          the viewer pane; the drawer closes itself first. */}
      {showDocs && (
        <MobileDocsDrawer
          theme={theme}
          scopedFetch={scopedFetch}
          onClose={() => setShowDocs(false)}
          onOpenDoc={(name) => {
            setShowDocs(false);
            setOpenDocFromDocs(name);
          }}
        />
      )}

      {/* Doc viewer pane — full-screen sheet for a single doc. Reused for
          both the in-chat doc-pill tap path and the docs-drawer card tap. */}
      {openDocFromDocs && (
        <MobileDocViewerPane
          filename={openDocFromDocs}
          theme={theme}
          scopedFetch={scopedFetch}
          onClose={() => setOpenDocFromDocs(null)}
        />
      )}
    </div>
  );
};
